import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface AIAnswerBlockProps {
  question: string;
  answer: string;
  bullets?: string[];
  className?: string;
}

export default function AIAnswerBlock({ question, answer, bullets, className = '' }: AIAnswerBlockProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className={`bg-white py-24 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* Top label bar */}
        <div className="flex items-center gap-3 mb-12">
          <div className="h-px flex-1 bg-stone-100" />
          <div className="inline-flex items-center gap-2 border border-stone-200 bg-stone-50 px-4 py-2 rounded-full flex-shrink-0">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-search-eye-line text-accent text-xs" />
            </div>
            <span className="text-stone-500 text-xs font-semibold tracking-widest uppercase">AI Search Overview</span>
          </div>
          <div className="h-px flex-1 bg-stone-100" />
        </div>

        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-20 items-start transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left: question heading */}
          <div>
            <h2
              className="font-serif text-3xl lg:text-4xl text-stone-900 leading-snug"
              style={{ letterSpacing: '-0.01em' }}
            >
              {question}
            </h2>
            {/* Gold line */}
            <div className="mt-6 w-10 h-0.5 bg-accent" />
          </div>

          {/* Right: answer + bullets */}
          <div>
            <p className="text-stone-600 text-base leading-relaxed mb-7">{answer}</p>
            {bullets && bullets.length > 0 && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-accent text-xs" />
                      </div>
                    </div>
                    <span className="text-stone-600 text-sm leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
